import React, { useEffect, useRef, useState } from 'react'

import { injectIntl, WrappedComponentProps } from 'react-intl'

import {
  FollowersBucket,
  IAgentSourcedCreatorFilters,
  ICreatorSearchFilters,
  Provider,
} from '../types'
import {
  FilterBarRow,
  FilterBarWrapper,
  FilterDropdown,
  FilterLabel,
  FilterValue,
  SearchField,
} from './FilterBar.styled'
import FilterDropdownComponent from './FilterDropdown'
import {
  AGE_RANGE_OPTIONS,
  ENGAGEMENT_RATE_OPTIONS,
  FOLLOWERS_OPTIONS,
  // LAST_POST_OPTIONS, // hidden for now (BRAND-9295)
  LOCATION_OPTIONS,
  VIEWS_OPTIONS,
} from './filterOptions'
import PlatformPills from './PlatformPills'

interface ICreatorSearchProps {
  mode: 'creator-search'
  filters: ICreatorSearchFilters
  onChange: (next: ICreatorSearchFilters) => void
}

interface IAgentSourcedCreatorFilterBarProps {
  mode: 'agent-sourced'
  filters: IAgentSourcedCreatorFilters
  onChange: (next: IAgentSourcedCreatorFilters) => void
}

type IProps = (ICreatorSearchProps | IAgentSourcedCreatorFilterBarProps) & WrappedComponentProps

const PLATFORM_OPTIONS: { value: Provider; label: string }[] = [
  { value: 'instagram', label: 'Instagram' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'youtube', label: 'YouTube' },
]

const FOLLOWERS_BUCKET_OPTIONS: { value: FollowersBucket; label: string }[] = [
  { value: 'lt_10k', label: '< 10,000' },
  { value: '10k_20k', label: '10,000 - 19,999' },
  { value: '20k_50k', label: '20,000 - 49,999' },
  { value: '50k_100k', label: '50,000 - 99,999' },
  { value: 'gt_100k', label: '100,000+' },
]

const formatPlatformValue = (platforms?: Provider[]): string => {
  if (!platforms || platforms.length === 0) return PLATFORM_OPTIONS.map((o) => o.label).join(', ')
  return platforms.map((p) => PLATFORM_OPTIONS.find((o) => o.value === p)?.label ?? p).join(', ')
}

const formatFollowersValue = (bucket?: FollowersBucket): string =>
  FOLLOWERS_BUCKET_OPTIONS.find((o) => o.value === bucket)?.label ?? 'Any'

const FilterBar: React.FC<IProps> = (props) => {
  const { intl } = props
  const [platformOpen, setPlatformOpen] = useState(false)
  const [followersOpen, setFollowersOpen] = useState(false)
  // Hoisted before early return to satisfy Rules of Hooks
  const [searchInput, setSearchInput] = useState(
    props.mode === 'creator-search' ? props.filters.search_query ?? '' : '',
  )
  // Ref keeps debounce callback up-to-date without re-scheduling the timer
  const propsRef = useRef(props)
  propsRef.current = props

  useEffect(() => {
    const handle = window.setTimeout(() => {
      const p = propsRef.current
      if (p.mode !== 'creator-search') return
      if (searchInput !== (p.filters.search_query ?? '')) {
        p.onChange({ ...p.filters, search_query: searchInput || undefined })
      }
    }, 300)
    return () => window.clearTimeout(handle)
  }, [searchInput])

  if (props.mode === 'agent-sourced') {
    const { filters, onChange } = props

    const set = <K extends keyof IAgentSourcedCreatorFilters>(
      key: K,
      value: IAgentSourcedCreatorFilters[K],
    ): void => {
      onChange({ ...filters, [key]: value })
    }

    return (
      <FilterBarWrapper>
        <PlatformPills selected={filters.platforms} onChange={(v) => set('platforms', v)} />
        <FilterBarRow>
          <FilterDropdownComponent
            mode="multi"
            labelKey="creatorSearch.filter.ageRange.label"
            options={AGE_RANGE_OPTIONS}
            value={filters.age_ranges}
            onChange={(v) => set('age_ranges', v)}
            testId="filter-age-range"
          />
          <FilterDropdownComponent
            mode="multi"
            labelKey="creatorSearch.filter.engagementRate.label"
            options={ENGAGEMENT_RATE_OPTIONS}
            value={filters.engagement_rate}
            onChange={(v) => set('engagement_rate', v)}
            testId="filter-engagement-rate"
          />
          <FilterDropdownComponent
            mode="multi"
            labelKey="creatorSearch.filter.followers.label"
            options={FOLLOWERS_OPTIONS}
            value={filters.followers}
            onChange={(v) => set('followers', v)}
            testId="filter-followers"
          />
        </FilterBarRow>
        <FilterBarRow>
          <FilterDropdownComponent
            mode="multi"
            labelKey="creatorSearch.filter.location.label"
            options={LOCATION_OPTIONS}
            value={filters.locations}
            onChange={(v) => set('locations', v)}
            testId="filter-location"
          />
          <FilterDropdownComponent
            mode="multi"
            labelKey="creatorSearch.filter.views.label"
            options={VIEWS_OPTIONS}
            value={filters.views}
            onChange={(v) => set('views', v)}
            testId="filter-views"
          />
          {/* Last Post filter hidden for now (BRAND-9295) — backend wiring TBD.
          <FilterDropdownComponent
            mode="single"
            labelKey="creatorSearch.filter.lastPost.label"
            options={LAST_POST_OPTIONS}
            value={filters.last_post_within}
            onChange={(v) => set('last_post_within', v)}
            testId="filter-last-post"
          />
          */}
        </FilterBarRow>
      </FilterBarWrapper>
    )
  }

  // creator-search mode
  const { filters, onChange } = props

  const togglePlatform = (value: Provider) => {
    const current = filters.platforms ?? []
    const next = current.includes(value) ? current.filter((p) => p !== value) : [...current, value]
    onChange({
      ...filters,
      platforms: next.length === 0 || next.length === PLATFORM_OPTIONS.length ? undefined : next,
    })
  }

  const setFollowersBucket = (bucket: FollowersBucket | undefined) => {
    onChange({ ...filters, followers_bucket: bucket })
  }

  return (
    <FilterBarWrapper>
      <SearchField
        type="search"
        placeholder={intl.formatMessage({ id: 'creatorSearch.filter.search.placeholder' })}
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        data-testid="creator-search-search-input"
      />
      <FilterDropdown
        onClick={() => {
          setPlatformOpen((v) => !v)
          setFollowersOpen(false)
        }}
        data-testid="creator-search-platform-filter"
      >
        <FilterLabel>
          {intl.formatMessage({ id: 'creatorSearch.filter.platform.label' })}
        </FilterLabel>
        <FilterValue>{formatPlatformValue(filters.platforms)}</FilterValue>
        {platformOpen && (
          <ul role="menu">
            {PLATFORM_OPTIONS.map((opt) => {
              const checked = (filters.platforms ?? []).includes(opt.value)
              return (
                <li
                  key={opt.value}
                  onClick={(e) => {
                    e.stopPropagation()
                    togglePlatform(opt.value)
                  }}
                >
                  <input type="checkbox" checked={checked} readOnly />
                  <span>{opt.label}</span>
                </li>
              )
            })}
          </ul>
        )}
      </FilterDropdown>
      <FilterDropdown
        onClick={() => {
          setFollowersOpen((v) => !v)
          setPlatformOpen(false)
        }}
        data-testid="creator-search-followers-filter"
      >
        <FilterLabel>
          {intl.formatMessage({ id: 'creatorSearch.filter.followers.label' })}
        </FilterLabel>
        <FilterValue>{formatFollowersValue(filters.followers_bucket)}</FilterValue>
        {followersOpen && (
          <ul role="menu">
            <li
              onClick={(e) => {
                e.stopPropagation()
                setFollowersBucket(undefined)
                setFollowersOpen(false)
              }}
            >
              <span>Any</span>
            </li>
            {FOLLOWERS_BUCKET_OPTIONS.map((opt) => (
              <li
                key={opt.value}
                onClick={(e) => {
                  e.stopPropagation()
                  setFollowersBucket(opt.value)
                  setFollowersOpen(false)
                }}
              >
                <span>{opt.label}</span>
              </li>
            ))}
          </ul>
        )}
      </FilterDropdown>
    </FilterBarWrapper>
  )
}

export { FilterBar }
export default injectIntl(FilterBar)
